import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import appendScriptTag from '../../utilities/appendScriptTag';
import Source from '../../models/source';
import SpotifyAuthenticationErrorObject from '../../models/spotify/authenticationErrorObject';
import SpotifyErrorObject from '../../models/spotify/errorObject';
import UserProfile from '../../models/spotify/userProfile';
import { ActivePlaylistContext } from '../activePlaylist/activePlaylist';
import { Context } from '../_globalContext/context';
import configureSpotifyApi, { getUserProfile } from './spotifyApi';
import * as spotifyAuth from './spotifyAuth';

export interface ISpotifyContext {
  /**
   * Signing in. On iOS this navigates the whole page away and the returned
   * promise never settles - see spotifyAuth. Only callers passing
   * `allowRedirect` can take that path, because being thrown out of the app is
   * a reasonable thing to happen when you press "Spotify Login" and not a
   * reasonable thing to happen when you press play.
   */
  authorize(options?: { allowRedirect?: boolean }): Promise<void>;
  /** Whether authorize() can complete here, or only by navigating away. */
  canAuthorizeInPlace: boolean;
  /** A sign-in is in flight. Buttons show it and refuse a second press. */
  isAuthorizing: boolean;
  logout(): void;
  /**
   * Set while signed in. The value is a snapshot - anything that needs to
   * *use* a token goes through spotifyApi, which asks for a fresh one on
   * every call. Consumers only ever test this for truthiness.
   */
  accessToken?: string;
  userProfile?: UserProfile;

  player?: Spotify.Player;
  playerId?: string;
  playbackState?: Spotify.PlaybackState;

  spotifyApi?: ReturnType<typeof configureSpotifyApi>;
}
export const SpotifyContext = React.createContext({} as ISpotifyContext);

interface Props {
  children: React.ReactNode;
}

export default function SpotifyService({ children }: Props) {
  const { callSnackbar } = useContext(Context);
  const ap = useContext(ActivePlaylistContext);

  const didAppendScriptTag = useRef(false);
  const popupCallback = useRef<(() => void) | undefined>(undefined);
  /**
   * Set the instant the popup says it is done, before anything is awaited.
   * The close-watcher below has to be able to tell "the user gave up" from
   * "it finished and closed itself", and those are microseconds apart.
   */
  const popupCompleted = useRef(false);

  const [accessToken, setAccessToken] = useState<string>();
  const [userProfile, setUserProfile] = useState<UserProfile>();

  const [player, setPlayer] = useState<Spotify.Player>();
  /** The same instance, readable from effects without depending on it. */
  const playerRef = useRef<Spotify.Player>(undefined);
  const [playerId, setPlayerId] = useState<string>();

  const [playbackState, setPlaybackState] = useState<Spotify.PlaybackState>();

  const logout = useCallback(function () {
    spotifyAuth.clearTokens();
    setUserProfile(undefined);
    setAccessToken(undefined);

    if (playerRef.current) {
      playerRef.current.disconnect();
      playerRef.current = undefined;
    }
    setPlayer(undefined);
    setPlayerId(undefined);
    setPlaybackState(undefined);
  }, []);

  const spotifyErrorHandler = useCallback(
    function (err: any) {
      if (err.error) {
        if (err.error.message) {
          const errObj = err.error as SpotifyErrorObject;

          // https://developer.spotify.com/documentation/web-api/#response-schema
          switch (errObj.status) {
            case 401:
              logout();
              callSnackbar('Authorization failed, please re-authenticate and try again.');
              break;

            case 400:
            case 403:
              callSnackbar(errObj.message);
              break;

            case 404:
            case 429:
              callSnackbar(
                'There was a problem with your Spotify request. Please try again later.',
              );
              break;

            case 500:
            case 502:
            case 503:
              callSnackbar('Spotify server error! Please try again later.');
              break;

            default:
              callSnackbar(errObj.message);
              break;
          }
        } else if (err.error.error_description) {
          const errObj = err.error as SpotifyAuthenticationErrorObject;

          logout();
          callSnackbar('Authorization failed, please re-authenticate and try again.');
          console.error(errObj);
        }
      } else {
        callSnackbar(err);
      }
    },
    [callSnackbar, logout],
  );

  const errorHandlerWrapper: <T>(cb: () => Promise<T>) => Promise<T> = useCallback(
    async function <T>(cb: () => Promise<T>) {
      try {
        return await cb();
      } catch (err) {
        spotifyErrorHandler(err);
        throw err;
      }
    },
    [spotifyErrorHandler],
  );

  const spotifyApi = useMemo(
    () =>
      accessToken
        ? configureSpotifyApi(spotifyAuth.getFreshAccessToken, errorHandlerWrapper)
        : undefined,
    [accessToken, errorHandlerWrapper],
  );

  /**
   * Reads whatever the token store holds and, if it is good, becomes signed
   * in. Runs on mount - which is also how a redirect sign-in lands, since
   * /spotify-redirect renders outside the service tree and this service
   * mounts fresh on the way back - and again whenever the popup says it is
   * done.
   */
  const adoptStoredToken = useCallback(async function () {
    const token = await spotifyAuth.getFreshAccessToken();

    if (!token) {
      return false;
    }

    // Profile first, token second: setting accessToken is what makes the app
    // start using the account, and it should not do that on a token /me
    // rejects. A dead token is an ordinary state - clear it and stay signed
    // out rather than surface an error at startup.
    try {
      setUserProfile(await getUserProfile(spotifyAuth.getFreshAccessToken));
    } catch {
      spotifyAuth.clearTokens();
      return false;
    }

    setAccessToken(token);

    return true;
  }, []);

  useEffect(
    function loadTokenFromStorage() {
      adoptStoredToken();
    },
    [adoptStoredToken],
  );

  useEffect(
    function listenForPopupCompletion() {
      async function messageHandler(e: MessageEvent) {
        if (
          e.origin !== window.location.origin ||
          !e.data ||
          e.data[spotifyAuth.popupDoneMessage] !== true
        ) {
          return;
        }

        popupCompleted.current = true;

        // The popup redeemed the code and wrote the token store itself, so
        // there is nothing to carry in the message. Just read what it left.
        await adoptStoredToken();

        if (popupCallback.current) {
          popupCallback.current();
        }
      }

      window.addEventListener('message', messageHandler, false);

      return function () {
        window.removeEventListener('message', messageHandler, false);
      };
    },
    [adoptStoredToken],
  );

  const doAuthorize = useCallback(async function (options?: {
    allowRedirect?: boolean;
  }): Promise<void> {
    if (spotifyAuth.willRedirect(options && options.allowRedirect)) {
      await spotifyAuth.beginRedirect();

      // The page is on its way to Spotify. Hand back a promise that never
      // settles so no caller runs a .then() against a document being torn
      // down - same contract the other providers have on this path.
      return new Promise<void>(function neverSettles() {});
    }

    popupCompleted.current = false;

    const popup = await spotifyAuth.beginPopup();

    return new Promise<void>(function waitForPopup(resolve, reject) {
      const poll = setInterval(function checkIfClosed() {
        // The old code tested `window.closed` here, which asks whether *this*
        // window has closed - it never has, so a cancelled sign-in hung
        // forever instead of rejecting.
        if (!popup.closed || popupCompleted.current) {
          return;
        }

        clearInterval(poll);
        popupCallback.current = undefined;
        reject(new Error('Spotify sign-in was cancelled.'));
      }, 300);

      popupCallback.current = function popupFinished() {
        clearInterval(poll);
        popupCallback.current = undefined;
        resolve();
      };
    });
  }, []);

  const [isAuthorizing, setAuthorizing] = useState(false);
  const authorizeInFlight = useRef<Promise<void>>(undefined);

  /**
   * One sign-in at a time - same shape as dropboxService.authorize, for the
   * same reason. A second press would open a second popup that the first
   * one's close-watcher knows nothing about. The second caller gets the first
   * promise instead, and isAuthorizing lets the button say so.
   *
   * beginPopup's window.open still runs synchronously inside the click: this
   * wrapper adds no await in front of doAuthorize.
   */
  const authorize = useCallback(
    function (options?: { allowRedirect?: boolean }): Promise<void> {
      if (authorizeInFlight.current) {
        return authorizeInFlight.current;
      }

      setAuthorizing(true);

      authorizeInFlight.current = doAuthorize(options).finally(function settled() {
        authorizeInFlight.current = undefined;
        setAuthorizing(false);
      });

      return authorizeInFlight.current;
    },
    [doAuthorize],
  );

  /**
   * Previously we registered a listener on player play cleanup
   * This listener listened for state changes and paused spotify when applicable
   *
   * This was needed because the cleanup spotifyPlayer.pause was sometimes resolving
   * before the spotifyPlayer PUT play track request finished.
   *
   * The aforementioned listener was however not reliable, maybe it was pausing before
   * the play and we weren't getting a correct state update after the play finished.
   * Maybe it was the state_conflict calls in the network log....
   *
   * @param state
   */
  useEffect(
    function doMonitorPlayerState() {
      let interval: NodeJS.Timeout | undefined;

      async function monitorPlayerState() {
        if (player) {
          const state = await player.getCurrentState();
          if (state) {
            setPlaybackState(state);

            if (!state.paused && ap.currentTrack.trackType !== Source.Spotify) {
              player.pause().catch(console.error);
            }
          }
        }
      }

      if (player) {
        monitorPlayerState();
        interval = setInterval(monitorPlayerState, 1000);
      }

      return function () {
        if (interval !== undefined) {
          clearInterval(interval);
        }
      };
    },
    [player, ap.currentTrack],
  );

  useEffect(
    function loadSpotifyWebPlaybackSdk() {
      function initPlayer() {
        // A logout followed by a login re-runs this with the SDK already on
        // the page; the player it made is gone (disconnected in logout), so a
        // new one is wanted. Two logins with no logout between would not get
        // here - accessToken is unchanged - but guard anyway: two connected
        // players would be two devices called "Fadila Web Player".
        if (playerRef.current) {
          return;
        }

        const player = new Spotify.Player({
          name: 'Fadila Web Player',
          // The SDK asks for a token when it connects and again whenever the
          // one it holds ages out. Handing it getFreshAccessToken is the whole
          // renewal story - there is no timer anywhere.
          getOAuthToken: (cb) => {
            spotifyAuth.getFreshAccessToken().then((token) => token && cb(token));
          },
        });
        playerRef.current = player;

        // Error handling
        player.addListener('initialization_error', ({ message }) => {
          spotifyErrorHandler(message);
        });
        player.addListener('authentication_error', ({ message }) => {
          spotifyErrorHandler(message);
        });
        player.addListener('account_error', ({ message }) => {
          // What a listener without Premium sees. The SDK's own text is terse.
          spotifyErrorHandler(
            'Spotify playback needs a Spotify Premium account. (' + message + ')',
          );
        });
        player.addListener('playback_error', ({ message }) => {
          // Skip errors on pause failures
          if (message !== 'Cannot perform operation; no list was loaded.') {
            spotifyErrorHandler(message);
          }
        });

        // Playback status updates
        player.addListener('player_state_changed', function debugLogPlayerStateChanged(state) {
          console.debug(state);
        });

        // Ready
        player.addListener('ready', ({ device_id }) => {
          setPlayerId(device_id);
        });

        // Not Ready
        player.addListener('not_ready', ({ device_id }) => {
          console.log('Device ID has gone offline', device_id);
          setPlayerId(undefined);
          player.connect();
        });

        // Connect to the player!
        player.connect().then((success) => {
          if (success) {
            console.debug('The Web Playback SDK successfully connected to Spotify!');
            setPlayer(player);

            if (process.env.NODE_ENV === 'development') {
              (window as any)['spotifyPlayer'] = player;
            }
          }
        });
      }

      if (accessToken) {
        if (window.Spotify) {
          // Script already loaded - the ready callback fired long ago and will
          // not fire again.
          initPlayer();
        } else {
          window.onSpotifyWebPlaybackSDKReady = initPlayer;

          if (!didAppendScriptTag.current) {
            appendScriptTag('https://sdk.scdn.co/spotify-player.js');
            didAppendScriptTag.current = true;
          }
        }
      }
    },
    [accessToken, spotifyErrorHandler],
  );

  const ctxValue = useMemo<ISpotifyContext>(
    () => ({
      authorize,
      canAuthorizeInPlace: !spotifyAuth.willRedirect(true),
      isAuthorizing,
      logout,
      accessToken,
      userProfile,

      player,
      playerId,
      playbackState,

      spotifyApi,
    }),
    [
      authorize,
      isAuthorizing,
      logout,
      accessToken,
      userProfile,
      player,
      playerId,
      playbackState,
      spotifyApi,
    ],
  );

  return <SpotifyContext.Provider value={ctxValue}>{children}</SpotifyContext.Provider>;
}
